/**
 * Admin Storage Management Module
 *
 * Provides CRUD for storage pools, disk operations (clone/resize),
 * and physical drive discovery display.
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import type { PhysicalDrive } from '../api/types.js';
import { escapeHtml, escapeAttr, formatBytes } from '../utils.js';
import { declareLoading, declareLoaded, confirmDialog, notificationDialog } from '../ui/dialogs.js';
import { openCreateDiskDialog } from '../vm/drives.js';

// Module state
let poolsTable: unknown = null;
let disksTable: unknown = null;
let drivesTable: unknown = null;
let selectedHost: string = '';  // '' = local

/**
 * Get the currently selected host ('' = local)
 */
export function getSelectedHost(): string {
  return selectedHost;
}

/**
 * Set the selected host and reload all tables
 */
export async function setStorageHost(host: string): Promise<void> {
  selectedHost = host;
  declareLoading();
  await Promise.all([fillPoolsTable(), fillDisksTable(), fillDrivesTable()]);
  declareLoaded();
}

// =============================================================================
// Storage Pools
// =============================================================================

/**
 * Fill the storage pools table
 */
export async function fillPoolsTable(): Promise<void> {
  const pools = await apiCall(api.listStoragePools(selectedHost || undefined), 'Failed to Load Storage Pools');
  if (!pools) return;

  const tableData = pools.map((pool) => {
    const safeName = escapeAttr(pool.name);
    const usedBytes = pool.total_bytes - pool.available_bytes;
    const usedPct = pool.total_bytes > 0 ? Math.round((usedBytes / pool.total_bytes) * 100) : 0;

    // Space bar
    const barColor = usedPct > 90 ? '#e74c3c' : usedPct > 75 ? '#f39c12' : '#27ae60';
    const spaceBar = `<div style="width:100px;height:10px;background:#eee;border-radius:3px;overflow:hidden">
      <div style="width:${usedPct}%;height:100%;background:${barColor}"></div>
    </div>
    <small>${formatBytes(pool.available_bytes)} free of ${formatBytes(pool.total_bytes)}</small>`;

    const actions = `
      <button class="ui-button" onclick='deletePool("${safeName}")'>Delete</button>
    `;

    return {
      name: escapeHtml(pool.name),
      path: escapeHtml(pool.path),
      space: spaceBar,
      disks: pool.disk_count ?? 0,
      actions,
    };
  });

  if (!poolsTable) {
    poolsTable = $('#pools-table').DataTable({
      data: tableData,
      columns: [
        { data: 'name', title: 'Name' },
        { data: 'path', title: 'Path' },
        { data: 'space', title: 'Space' },
        { data: 'disks', title: 'Disks' },
        { data: 'actions', title: 'Actions' },
      ],
      paging: false,
      info: false,
      searching: false,
    });
  } else {
    const dt = poolsTable as { clear: () => unknown; rows: { add: (data: unknown) => unknown }; draw: () => void };
    dt.clear();
    dt.rows.add(tableData);
    dt.draw();
  }
}

/**
 * Create a new storage pool
 */
export async function createPool(): Promise<void> {
  const form = `<div class="vm-form">
    <label>Directory Path</label>
    <div class="form-value"><input id="pool-path" name="pool-path" placeholder="/path/to/storage"></div>
    <label>Name (optional)</label>
    <div class="form-value"><input id="pool-name" name="pool-name" placeholder="auto-derived from path"></div>
    <p style="font-size: 0.9em; color: #666; grid-column: 1 / -1;">
      Enter an absolute path to a directory. Only one pool per filesystem is allowed.
    </p>
  </div>`;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Add Storage Pool</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Add Storage Pool',
      width: 500,
      buttons: {
        Add: async function () {
          const path = ($('#pool-path').val() as string).trim();
          const name = ($('#pool-name').val() as string).trim() || undefined;
          if (!path) {
            alert('Please enter a path');
            return;
          }
          if (!path.startsWith('/')) {
            alert('Path must be absolute (start with /)');
            return;
          }
          const parent = $(this);
          declareLoading();
          const result = await apiCall(api.createStoragePool(path, name, selectedHost || undefined), 'Failed to Add Pool');
          declareLoaded();
          if (result) {
            parent.dialog('close');
            fillPoolsTable();
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Delete a storage pool
 */
export async function deletePool(name: string): Promise<void> {
  confirmDialog(`Are you sure you want to remove the storage pool "${escapeHtml(name)}"? This will not delete any files.`, async () => {
    declareLoading();
    await apiCall(api.deleteStoragePool(name, selectedHost || undefined), 'Failed to Delete Pool');
    await fillPoolsTable();
    declareLoaded();
  });
}

// =============================================================================
// Disk Images
// =============================================================================

/**
 * Fill the disk images table
 */
export async function fillDisksTable(): Promise<void> {
  const disks = await apiCall(api.listDisks(selectedHost || undefined), 'Failed to Load Disks');
  if (!disks) return;

  const tableData = disks.map((disk) => {
    const safePool = escapeAttr(disk.pool || '');
    const safeName = escapeAttr(disk.name);
    const statusBadge = disk.is_base
      ? '<span class="badge badge-info">Base Image</span>'
      : disk.in_use
        ? `<span class="badge badge-warning">In use by ${escapeHtml(disk.vm_name || 'VM')}</span>`
        : '<span class="badge badge-success">Available</span>';

    const isQcow2 = disk.format?.toLowerCase() === 'qcow2';

    const formatDisplay = disk.encrypted
      ? `${escapeHtml(disk.format || 'unknown')} <span class="badge badge-locked">Encrypted</span>`
      : escapeHtml(disk.format || 'unknown');

    // Actions - clone always available, resize only for qcow2 and not in use/base
    let actions = `<button class="ui-button" onclick='cloneDisk("${safePool}","${safeName}",${!!disk.encrypted},${disk.size_bytes || 0},${!!disk.is_base})'>Clone</button>`;
    if (isQcow2 && !disk.is_base && !(disk.in_use && disk.vm_running)) {
      actions += ` <button class="ui-button" onclick='resizeDisk("${safePool}","${safeName}")'>Resize</button>`;
    }

    return {
      name: escapeHtml(disk.name),
      pool: escapeHtml(disk.pool || ''),
      size: formatBytes(disk.size_bytes),
      format: formatDisplay,
      status: statusBadge,
      actions,
    };
  });

  if (!disksTable) {
    disksTable = $('#disks-table').DataTable({
      data: tableData,
      columns: [
        { data: 'name', title: 'Name' },
        { data: 'pool', title: 'Pool' },
        { data: 'size', title: 'Size' },
        { data: 'format', title: 'Format' },
        { data: 'status', title: 'Status' },
        { data: 'actions', title: 'Actions' },
      ],
      paging: true,
      pageLength: 25,
      info: true,
      searching: true,
    });
  } else {
    const dt = disksTable as { clear: () => unknown; rows: { add: (data: unknown) => unknown }; draw: () => void };
    dt.clear();
    dt.rows.add(tableData);
    dt.draw();
  }
}

/**
 * Create a new disk image — opens the shared create-disk dialog.
 */
export async function createDisk(): Promise<void> {
  await openCreateDiskDialog(selectedHost || undefined, () => {
    fillDisksTable();
  });
}

/**
 * Clone a disk image
 */
export async function cloneDisk(sourcePool: string, sourceImage: string, sourceEncrypted?: boolean, sourceSize?: number, sourceBase?: boolean): Promise<void> {
  // Load pools for target selection (from the same host)
  const pools = await apiCall(api.listStoragePools(selectedHost || undefined), 'Failed to Load Pools');
  if (!pools) return;

  const sourceName = sourceImage.replace(/\.[^.]+$/, '') || 'disk';
  const defaultName = `${sourceName}-clone`;

  // Build pool options (value = pool name)
  let poolOptions = '';
  for (const pool of pools) {
    poolOptions += `<option value="${escapeAttr(pool.name)}">${escapeHtml(pool.name)} (${formatBytes(pool.available_bytes)} free)</option>`;
  }

  const encryptionSection = sourceEncrypted ? `
    <div id="clone-encryption-section" style="grid-column: 1 / -1;">
      <hr>
      <p style="font-size: 0.9em; color: #b94a48;">
        Source disk is encrypted (LUKS).
      </p>
      <div id="clone-passphrase-section" style="display: none;">
        <label>Source Passphrase</label>
        <div class="form-value">
          <input type="password" id="clone-passphrase" placeholder="Required for full copy">
        </div>
        <label style="display: inline-flex; align-items: center; gap: 0.5em; cursor: pointer;">
          <input type="checkbox" id="clone-rekey">
          Re-encrypt with new passphrase
        </label>
        <div id="clone-new-passphrase-section" style="display: none;">
          <label>New Passphrase</label>
          <div class="form-value">
            <input type="password" id="clone-new-passphrase">
          </div>
        </div>
      </div>
      <p id="clone-cow-encrypted-note" style="font-size: 0.9em; color: #666;">
        COW clone inherits the source's encryption &mdash; same passphrase required.
      </p>
    </div>` : `
    <div id="clone-encryption-section" style="grid-column: 1 / -1; display: none;">
      <hr>
      <label style="display: inline-flex; align-items: center; gap: 0.5em; cursor: pointer;">
        <input type="checkbox" id="clone-encrypt">
        Encrypt clone (LUKS)
      </label>
      <div id="clone-new-passphrase-section" style="display: none;">
        <label>Passphrase</label>
        <div class="form-value">
          <input type="password" id="clone-new-passphrase" placeholder="Required for encryption">
        </div>
      </div>
    </div>`;

  const form = `<div class="vm-form">
    <label>Clone Name</label>
    <div class="form-value"><input id="clone-name" value="${escapeAttr(defaultName)}"></div>
    <label>Target Pool</label>
    <div class="form-value"><select id="clone-pool">${poolOptions}</select></div>
    <label>Clone Type</label>
    <div class="form-value">
      <label style="display: inline-flex; align-items: center; gap: 0.5em; cursor: pointer;">
        <input type="checkbox" id="clone-full-copy">
        Full copy (independent duplicate)
      </label>
    </div>
    <label>Base Image</label>
    <div class="form-value">
      <label style="display: inline-flex; align-items: center; gap: 0.5em; cursor: pointer;">
        <input type="checkbox" id="clone-as-base">
        Save as base image (template for COW clones)
      </label>
    </div>
    <p id="clone-help" style="font-size: 0.9em; color: #666; grid-column: 1 / -1;">
      Copy-on-write clone: Fast, space-efficient, but depends on the source disk.
    </p>
    ${encryptionSection}
  </div>`;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Clone Disk</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Clone Disk',
      width: 450,
      open: function () {
        function updateCloneHelp() {
          const isFullCopy = $('#clone-full-copy').is(':checked');
          const isBase = $('#clone-as-base').is(':checked');
          if (isBase) {
            $('#clone-help').text(
              isFullCopy
                ? 'Creates an independent base image. Other disks can be COW-cloned from it. Cannot be attached to VMs directly.'
                : 'Creates a COW base image that depends on the source. Other disks can be COW-cloned from it. Cannot be attached to VMs directly.'
            );
          } else {
            $('#clone-help').text(
              isFullCopy
                ? 'Full copy: Creates an independent duplicate. Slower and uses more space, but the clone does not depend on the source.'
                : 'Copy-on-write clone: Fast, space-efficient, but depends on the source disk.'
            );
          }
          // Toggle encryption sub-sections based on full_copy
          if (sourceEncrypted) {
            $('#clone-passphrase-section').toggle(isFullCopy);
            $('#clone-cow-encrypted-note').toggle(!isFullCopy);
          } else {
            // Non-encrypted source: show encrypt option only for full copy
            $('#clone-encryption-section').toggle(isFullCopy);
            if (!isFullCopy) {
              $('#clone-encrypt').prop('checked', false);
              $('#clone-new-passphrase-section').hide();
            }
          }
        }
        $('#clone-full-copy').on('change', updateCloneHelp);
        $('#clone-as-base').on('change', updateCloneHelp);
        // Toggle re-key passphrase field (encrypted source)
        $('#clone-rekey').on('change', function () {
          $('#clone-new-passphrase-section').toggle($(this).is(':checked'));
        });
        // Toggle encrypt passphrase field (non-encrypted source)
        $('#clone-encrypt').on('change', function () {
          $('#clone-new-passphrase-section').toggle($(this).is(':checked'));
        });
      },
      buttons: {
        Clone: async function () {
          const name = ($('#clone-name').val() as string).trim();
          const pool = $('#clone-pool').val() as string;
          const fullCopy = $('#clone-full-copy').is(':checked');
          const asBase = $('#clone-as-base').is(':checked');
          if (!name) {
            alert('Please enter a name');
            return;
          }
          // Validate encryption inputs
          let passphrase: string | undefined;
          let newPassphrase: string | undefined;
          if (sourceEncrypted && fullCopy) {
            passphrase = ($('#clone-passphrase').val() as string || '').trim();
            if (!passphrase) {
              alert('Passphrase is required for full copy of an encrypted disk');
              return;
            }
            if ($('#clone-rekey').is(':checked')) {
              newPassphrase = ($('#clone-new-passphrase').val() as string || '').trim();
              if (!newPassphrase) {
                alert('Please enter a new passphrase for re-encryption');
                return;
              }
            }
          } else if (!sourceEncrypted && fullCopy && $('#clone-encrypt').is(':checked')) {
            newPassphrase = ($('#clone-new-passphrase').val() as string || '').trim();
            if (!newPassphrase) {
              alert('Please enter a passphrase for encryption');
              return;
            }
          }
          const dlg = $(this);
          const btnPane = dlg.closest('.ui-dialog').find('.ui-dialog-buttonpane');

          // Replace dialog body with progress UI
          dlg.html(`
            <div style="text-align: center; padding: 1.5em 1em;">
              <div class="form-spinner" style="width: 32px; height: 32px; margin: 0 auto 1em;
                border: 3px solid var(--color-loader-bg); border-top-color: var(--color-loader-spinner);
                border-radius: 50%; animation: spin 0.8s linear infinite;"></div>
              <div id="clone-progress-status" style="margin-bottom: 0.5em; color: var(--color-text-muted);">
                Cloning disk...
              </div>
              <div class="clone-progress-bar" id="clone-progress-bar" style="display: none;">
                <div class="clone-progress-fill" id="clone-progress-fill"></div>
              </div>
              <div id="clone-progress-size" style="font-size: 0.85em; color: var(--color-text-light); margin-top: 0.5em;"></div>
            </div>
          `);

          // Hide Clone button, keep Cancel
          btnPane.find('button').each(function () {
            if ($(this).text() === 'Clone') $(this).hide();
          });

          // Start the clone API call (don't await yet)
          const clonePromise = api.cloneDisk(
            sourcePool, sourceImage, name, pool, fullCopy,
            selectedHost || undefined, asBase, sourceBase,
            passphrase, newPassphrase
          );

          // Start progress polling for full-copy operations with known source size
          let pollInterval: ReturnType<typeof setInterval> | null = null;
          if (fullCopy && sourceSize && sourceSize > 0) {
            pollInterval = setInterval(async () => {
              try {
                const stat = await api.getDiskStat(pool, name, selectedHost || undefined, asBase);
                if (stat.exists && stat.size_bytes > 0) {
                  const pct = Math.min(99, Math.round((stat.size_bytes / sourceSize) * 100));
                  $('#clone-progress-bar').show();
                  $('#clone-progress-fill').css('width', pct + '%');
                  $('#clone-progress-size').text(
                    `${formatBytes(stat.size_bytes)} / ${formatBytes(sourceSize)}`
                  );
                }
              } catch {
                // Stat poll failure is non-fatal — clone may still be in progress
              }
            }, 2000);
          }

          // Await the clone result
          try {
            const result = await clonePromise;
            if (pollInterval) clearInterval(pollInterval);
            // Show 100% briefly before closing
            $('#clone-progress-bar').show();
            $('#clone-progress-fill').css('width', '100%');
            $('#clone-progress-status').text('Clone complete!');
            $('#clone-progress-size').text(formatBytes(result.size_bytes));
            setTimeout(() => {
              dlg.dialog('close');
              notificationDialog(`Disk cloned successfully: ${result.path}`);
              fillDisksTable();
            }, 600);
          } catch (error) {
            if (pollInterval) clearInterval(pollInterval);
            const msg = error instanceof Error ? error.message : String(error);
            dlg.html(`
              <div style="text-align: center; padding: 1.5em 1em;">
                <div style="color: var(--color-error-text); font-size: 1.1em; margin-bottom: 0.5em;">Clone failed</div>
                <div style="color: var(--color-text-muted);">${escapeHtml(msg)}</div>
              </div>
            `);
            // Re-show a Close button
            btnPane.find('button').each(function () {
              if ($(this).text() === 'Cancel') $(this).text('Close');
            });
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Resize a disk image
 */
export async function resizeDisk(storagePool: string, imageName: string): Promise<void> {
  const form = `<div class="vm-form">
    <label>New Size (GB)</label>
    <div class="form-value"><input type="number" id="resize-gb" min="1" value="100"></div>
    <p style="font-size: 0.9em; color: #666; grid-column: 1 / -1;">
      Disk can only grow, not shrink. VM must be stopped.
    </p>
  </div>`;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Resize Disk</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Resize Disk',
      width: 400,
      buttons: {
        Resize: async function () {
          const sizeGb = parseInt($('#resize-gb').val() as string) || 0;
          if (sizeGb < 1) {
            alert('Please enter a valid size');
            return;
          }
          const parent = $(this);
          declareLoading();
          const result = await apiCall(api.resizeDisk(storagePool, imageName, sizeGb, selectedHost || undefined), 'Failed to Resize Disk');
          declareLoaded();
          if (result) {
            parent.dialog('close');
            notificationDialog(`Disk resized to ${result.new_size_gb} GB`);
            fillDisksTable();
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

// =============================================================================
// Physical Drives
// =============================================================================

/**
 * Fill the physical drives table
 */
export async function fillDrivesTable(): Promise<void> {
  const drives = await apiCall(api.listPhysicalDrives(selectedHost || undefined), 'Failed to Load Drives');
  if (!drives) return;

  const tableData = drives.map((drive: PhysicalDrive) => {
    let statusBadge: string;
    if (drive.mounted) {
      statusBadge = '<span class="badge badge-danger">Mounted</span>';
    } else if (drive.holder) {
      statusBadge = `<span class="badge badge-warning">Held by ${escapeHtml(drive.holder)}</span>`;
    } else if (drive.in_use) {
      statusBadge = `<span class="badge badge-warning">In use by ${escapeHtml(drive.vm_name || 'VM')}</span>`;
    } else {
      statusBadge = '<span class="badge badge-success">Available</span>';
    }

    return {
      device: escapeHtml(drive.path),
      model: escapeHtml(drive.model || ''),
      size: drive.size_human,
      status: statusBadge,
    };
  });

  if (!drivesTable) {
    drivesTable = $('#drives-table').DataTable({
      data: tableData,
      columns: [
        { data: 'device', title: 'Device' },
        { data: 'model', title: 'Model' },
        { data: 'size', title: 'Size' },
        { data: 'status', title: 'Status' },
      ],
      paging: false,
      info: false,
      searching: false,
    });
  } else {
    const dt = drivesTable as { clear: () => unknown; rows: { add: (data: unknown) => unknown }; draw: () => void };
    dt.clear();
    dt.rows.add(tableData);
    dt.draw();
  }
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).fillPoolsTable = fillPoolsTable;
  (window as unknown as Record<string, unknown>).fillDisksTable = fillDisksTable;
  (window as unknown as Record<string, unknown>).fillDrivesTable = fillDrivesTable;
  (window as unknown as Record<string, unknown>).createPool = createPool;
  (window as unknown as Record<string, unknown>).deletePool = deletePool;
  (window as unknown as Record<string, unknown>).createDisk = createDisk;
  (window as unknown as Record<string, unknown>).cloneDisk = cloneDisk;
  (window as unknown as Record<string, unknown>).resizeDisk = resizeDisk;
  (window as unknown as Record<string, unknown>).setStorageHost = setStorageHost;
}
